import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { 
  QrCode, 
  Check, 
  FileText, 
  Link, 
  Search, 
  Signature, 
  Hash,
  Upload, 
  Calendar, 
  DollarSign, 
  Tag,
  Trash,
  Edit,
  Settings
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import CertificateForm from "./CertificateForm";
import CertificateTemplateManager from "./CertificateTemplateManager";
import NewCertificateForm from "./NewCertificateForm";
import { useCertificateStore, Certificate } from "@/services/certificateService";

const AdminCertificates: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [validationNumber, setValidationNumber] = useState("");
  const [validationResult, setValidationResult] = useState<Certificate | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  
  // Usar o store de certificados
  const { 
    certificates, 
    addCertificate, 
    updateCertificate, 
    deleteCertificate 
  } = useCertificateStore();

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast("Por favor, insira um termo para buscar");
      return;
    }

    toast(`Buscando por: ${searchTerm}`);
  };

  const handleCertificateCreated = (newCertificate: any) => {
    // Gerar ID único
    const newId = `cert-${Date.now()}`;
    
    // Criar novo certificado com o formato compatível
    const certificate: Certificate = {
      id: newId,
      name: newCertificate.nomeTitular || "",
      organization: newCertificate.organizacao || "",
      project: newCertificate.projeto || "",
      date: new Date().toISOString().split('T')[0],
      tokenId: `TKN${Math.floor(Math.random() * 10000000)}`,
      status: "Active",
      creditAmount: parseInt(newCertificate.quantidadeCreditos) || 0,
      creditType: newCertificate.tipoCredito || "VCS",
      validityPeriod: newCertificate.periodoValidade || "",
      verificationURL: `https://banclima.com.br/verify/${newId}`,
      verificationMethod: "QR Code + Blockchain",
      serialNumber: `BR-${newCertificate.tipoCredito?.substring(0, 3) || "VCS"}-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`,
      issuer: "BanClima Certificadora",
      description: newCertificate.descricao || `Certificado emitido para o projeto ${newCertificate.projeto} da ${newCertificate.nomeTitular}, com redução de emissões de ${newCertificate.quantidadeCreditos}.`,
      location: {
        city: newCertificate.cidade || "",
        state: newCertificate.estado || "",
        country: "Brasil"
      },
      downloadUrl: "/certificates/new-certificate.pdf",
      certificateImage: "/certificates/default-certificate.jpg"
    };
    
    // Adicionar ao store global
    addCertificate(certificate);
    
    // Mostrar mensagem de sucesso
    toast(`Certificado criado com sucesso. ID: ${newId}`);
    
    // Fechar formulário
    setShowForm(false);
  };

  const handleValidateCertificate = () => {
    if (!validationNumber.trim()) {
      toast("Por favor, insira um número de certificado para validar");
      return;
    }

    const found = certificates.find(cert => cert.serialNumber === validationNumber || cert.id === validationNumber);
    
    if (found) {
      setValidationResult(found);
      toast(`Certificado ${validationNumber} é válido.`);
    } else {
      setValidationResult(null);
      toast(`Certificado ${validationNumber} não encontrado ou inválido.`);
    }
  };

  const handleDeleteCertificate = (id: string) => {
    if (confirm("Tem certeza que deseja excluir este certificado? Esta ação não pode ser desfeita.")) {
      deleteCertificate(id);
      toast("Certificado excluído com sucesso");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCertificateFile(e.target.files[0]);
      toast(`Arquivo selecionado: ${e.target.files[0].name}`);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Filtrar certificados baseado no termo de busca
  const filteredCertificates = searchTerm.trim() === "" 
    ? certificates 
    : certificates.filter(cert => 
        cert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.project.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return (
    <div className="p-6 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Certificados</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowTemplateManager(!showTemplateManager)}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            {showTemplateManager ? 'Ocultar Templates' : 'Gerenciar Templates'}
          </Button>
        </div>
      </div>

      {showTemplateManager && (
        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <CertificateTemplateManager />
        </div>
      )}

      {!showForm ? (
        <>
          <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-6">
            <h2 className="text-lg font-semibold mb-4">Registro de Certificados</h2>
            <Button className="mb-4 bg-[#0e7cb4] text-white" onClick={() => setShowForm(true)}>
              Novo Certificado
            </Button>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Projeto</TableHead>
                  <TableHead>Organização</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCertificates.map((cert) => (
                  <TableRow key={cert.id}>
                    <TableCell className="font-medium">{cert.id}</TableCell>
                    <TableCell>{cert.name}</TableCell>
                    <TableCell>{cert.project}</TableCell>
                    <TableCell>{cert.organization}</TableCell>
                    <TableCell>{cert.date}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cert.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {cert.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setShowForm(true)}>
                        <Edit className="h-4 w-4 mr-1" /> Editar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCertificate(cert.id)}>
                        <Trash className="h-4 w-4 mr-1" /> Excluir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

      {/* Certificate Validation Section (agora abaixo) */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <h2 className="text-lg font-semibold mb-4">Validação de Certificados</h2>
        <div className="flex gap-2">
          <Input
            value={validationNumber}
            onChange={(e) => setValidationNumber(e.target.value)}
            placeholder="Digite o número ou ID do certificado"
            className="max-w-md"
          />
          <Button onClick={handleValidateCertificate}>
            <Search className="h-4 w-4 mr-2" /> Validar
          </Button>
        </div>

        {validationResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <h3 className="font-semibold text-green-800">Certificado Válido</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-sm font-semibold">ID do Certificado:</p>
                <p className="text-sm">{validationResult.id}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Número de Série:</p>
                <p className="text-sm">{validationResult.serialNumber}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Nome:</p>
                <p className="text-sm">{validationResult.name}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Organização:</p>
                <p className="text-sm">{validationResult.organization}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Projeto:</p>
                <p className="text-sm">{validationResult.project}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Tipo de Crédito:</p>
                <p className="text-sm">{validationResult.creditType}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Quantidade:</p>
                <p className="text-sm">{validationResult.creditAmount} tCO₂e</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Período de Validade:</p>
                <p className="text-sm">{validationResult.validityPeriod}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Data de Emissão:</p>
                <p className="text-sm">{validationResult.date}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Status:</p>
                <p className="text-sm">{validationResult.status}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Emissor:</p>
                <p className="text-sm">{validationResult.issuer}</p>
              </div>
              <div>
                <p className="text-sm font-semibold">Token ID:</p>
                <p className="text-sm">{validationResult.tokenId}</p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm font-semibold">Descrição:</p>
                <p className="text-sm">{validationResult.description}</p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm font-semibold">Localização:</p>
                <p className="text-sm">
                  {validationResult.location.city}, {validationResult.location.state}, {validationResult.location.country}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

        </>
      ) : (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Novo Certificado</h2>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Voltar
            </Button>
          </div>
          <NewCertificateForm 
            onSubmit={handleCertificateCreated} 
            onCancel={() => setShowForm(false)} 
          />
        </div>
      )}
    </div>
  );
};

export default AdminCertificates;
